// property-search.component.ts
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgGridModule } from 'ag-grid-angular';
import { ColDef, GridApi, GridReadyEvent, ProcessCellForExportParams } from 'ag-grid-community';
import { ServerGridCore } from '../../models/BaseServerGrid';
import { PropertySearchRow } from '../../models/ratings.type';
import { RatingsService } from '../../shared/services/ratings/ratings.service';
import { compareDates, dateFilterComparator, formatDMY } from '../../shared/utils/date-helpers';
import { UserService } from '../../shared/services/user.service';
import { Router } from '@angular/router';
import { BookmarksComponent } from "../bookmarks/bookmarks.component";
import { MatSidenavModule } from "@angular/material/sidenav";
import { AppraisalDataService } from "../../shared/services/appraisal-data.service";

@Component({
  selector: 'app-property-search',
  standalone: true,
  imports: [CommonModule, AgGridModule, MatSidenavModule, BookmarksComponent],
  templateUrl: './property-search.component.html',
  styleUrls: ['./property-search.component.scss']
})
export class PropertySearchComponent extends ServerGridCore<PropertySearchRow> {
  bookmarkGrid: string = "property-search";
  override api!: GridApi;
  override columnDefs: ColDef[] = [
    { field: 'companyName', headerName: 'Company', hide: true, filter: 'agTextColumnFilter', minWidth: 200 },
    { field: 'countryName', headerName: 'Country', filter: 'agTextColumnFilter' },
    { field: 'countyName', headerName: 'County', filter: 'agTextColumnFilter' },
    { field: 'cityName', headerName: 'City', filter: 'agTextColumnFilter' },
    { field: 'divisionName', headerName: 'Division', filter: 'agTextColumnFilter' },
    { field: 'hereditamentReference', headerName: 'Hereditament Ref', filter: 'agTextColumnFilter' },
    { field: 'branchName', headerName: 'Branch', filter: 'agTextColumnFilter' },
    { field: 'clientReference', headerName: 'Client Ref', filter: 'agTextColumnFilter' },
    { field: 'addressPrimary', headerName: 'Address', filter: 'agTextColumnFilter', minWidth: 220 },
    { field: 'zipCode', headerName: 'Zip Code', filter: 'agTextColumnFilter' },
    {
      field: 'liabilityEndDate',
      headerName: 'Liability End',
      sortable: true,
      // filter: 'agDateColumnFilter',
      valueFormatter: p => formatDMY(p.value),
      comparator: compareDates,
      filterParams: {
        comparator: dateFilterComparator,
        browserDatePicker: true,              // show picker
        buttons: ['reset', 'apply'],
        debounceMs: 0
      }
    },
    { field: 'revaluationYear', headerName: 'Revaluation Period', hide: true, filter: 'agTextColumnFilter' }
  ];

  excelStyles = [
    { id: 'header', font: { bold: true } },
    { id: 'date', dataType: 'dateTime', numberFormat: { format: 'dd-mm-yyyy' } }
  ];

  userData: any;
  constructor(private apiSvc: RatingsService, private userService: UserService, public router: Router,
      private appraisalDataService: AppraisalDataService
    ) { super(); }
  
  async ngOnInit() {
    this.userService.userData$.subscribe((data) => {
      this.userData = data;
      if (!this.userData.roles.includes('Ratings')) {
        this.router.navigate(['/404']);
      }
    });
  }

  onGridReady(e: GridReadyEvent) {
    this.api = e.api;
    this.api.setGridOption('serverSideDatasource', this.makeDatasource());
  }

  onExport() {
    if (!this.api) return;

    const columnKeys = this.api.getAllDisplayedColumns().map(c => c.getColId());
    const today = new Date();
    const fileName =
      `property_search_${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}.xlsx`;
    const hasExcel = typeof (this.api as any).exportDataAsExcel === 'function';

    const processCell = (p: ProcessCellForExportParams) => {
      const f = p.column.getColDef().field;
      if (f === 'liabilityEndDate' && p.value) {
        const d = new Date(p.value);
        if (!isNaN(d.getTime())) {
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
      }
      return p.value;
    };

    if (hasExcel) {
      (this.api as any).exportDataAsExcel({
        fileName,
        sheetName: 'Property Search',
        columnKeys,
        processCellCallback: processCell,
        excelStyles: this.excelStyles,
        // SSRM note: exports rows currently loaded on the client.
      });
    } else {
      this.api.exportDataAsCsv({
        fileName: fileName.replace('.xlsx', '.csv'),
        columnKeys,
        processCellCallback: processCell
      });
    }
  }

  override project(): string[] {
    const cols = this.api?.getAllDisplayedColumns() || [];
    const map: Record<string, string> = {
      id: 'ID', clientReference: 'ClientReference', countryName: 'CountryName', countyName: 'CountyName', cityName: 'CityName',
      divisionName: 'DivisionName', hereditamentReference: 'HereditamentReference', branchName: 'BranchName',
      addressPrimary: 'AddressPrimary', zipCode: 'ZipCode', liabilityEndDate: 'LiabilityEndDate',
      revaluationID: 'RevaluationID', revaluationYear: 'RevaluationYear', companyName: 'CompanyName'
    };
    return cols.map(c => map[c.getColDef().field!] || c.getColDef().field!);
  }

  override query(body: any) { return this.apiSvc.propertySearchQuery(body); }

  isBookmarksPanelOpen: boolean = false;
  ontoggleBookmarkSidenav() {
    let state = this.api.getState();
    this.appraisalDataService.setGridStateData(state);
    this.isBookmarksPanelOpen = !this.isBookmarksPanelOpen;
  }

  loadBookmark() {
    let state = this.appraisalDataService.getGridStateData();
    this.api.setState(state);
  }
}
